// Copyright (c) Docugami, Inc. All rights reserved.

import { ComponentId } from "sdv/engine/Component";
import { IEntityManager } from "sdv/engine/Entity";
import { ISystem, Queries } from "../engine/Engine";
import { IResources, PageData, ResourceDependencies } from "../Resources";
import { InputState } from "sdv/input/InputController";
import { MutableRefObject } from "react";
import { TokenRange } from "networking/api";
import { getPageForToken, Page, PageAndRange } from "sdv/Page";
import { createBoundsForTokenRange } from "sdv/utils/TokenBounds";
import { TokenIdToBox, combineBoxes } from "store/shared_types";
import { rangesOverlap } from "utils/MathUtils";
import { FocusViewComponent } from "sdv/components/FocusViewComponent";
import { TokenRangeComponent } from "sdv/components/TokenRangeComponent";
import { scaleValue } from "sdv/Space";
import { ViewComponent } from "sdv/components/ViewComponent";

const focusedPageResources: ResourceDependencies = ["viewPosition", "viewHeight", "visiblePages"];

/**
 * Focuses the canvas view position on a target token range. Also updates the currently focused
 * page which drives the thumbnail visualizations.
 */
export class FocusSystem implements ISystem {
    private _scroller: MutableRefObject<HTMLDivElement | undefined>;

    get updateEnabled() { return true; }

    resources!: IResources;
    entities!: IEntityManager;
    input!: InputState;

    constructor(scroller: MutableRefObject<HTMLDivElement | undefined>) {
        this._scroller = scroller;
    }

    queries: Queries = {
        focusTokenRanges: { types: [ComponentId.FocusView, ComponentId.TokenRange] },
        view: { types: [ComponentId.View] }
    }

    update() {
        this.updateFocusedPage();
        this.handleFocusTokenRangeRequests();
    }

    private updateFocusedPage(): void {
        if (!this.resources.check(focusedPageResources)) {
            return;
        }

        const view = this.queries.view.query!.single()?.getComponent<ViewComponent>(ComponentId.View);
        if (!view) {
            return;
        }

        const resourceValues = this.resources.values(focusedPageResources);

        let amountOnScreen: number = 0;
        let focusedPage: Page | null = null;
        const viewTop = resourceValues.viewPosition!.y;
        const viewBottom = viewTop + view.viewport.worldHeight

        for (const page of resourceValues.visiblePages!) {
            const pageTop = page.position.y;
            const pageBottom = pageTop + page.height;

            const amount = Math.max(Math.min(viewBottom, pageBottom) - Math.max(viewTop, pageTop), 0)
            if (amount > amountOnScreen) {
                amountOnScreen = amount;
                focusedPage = page;
            }
        }

        this.resources.set("focusedPage", focusedPage);
    }

    private handleFocusTokenRangeRequests(): void {
        if (!this.resources.get("pagesLoaded").value || !this._scroller.current) {
            return;
        }

        const focusRequests = this.queries.focusTokenRanges.query!;
        const focusEntity = focusRequests.last();
        let complete = true;
        if (!!focusEntity) {
            const tokenRange = focusEntity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
            const scrollToTop: boolean = !!focusEntity.getComponent<FocusViewComponent>(ComponentId.FocusView)!.scrollToTop;
            complete = this.focusTokenRange(tokenRange, scrollToTop);
        }

        focusRequests.entities.forEach(e => {
            if (complete || focusEntity !== e) {
                e.destroy();
            }
        });
    }

    private focusTokenRange(range: TokenRange, scrollToTop: boolean): boolean {
        // Find the page containing the fromTok of the range
        const pages = this.resources.get("pages").value;
        let token = range.fromTok;
        let pageAndRange: PageAndRange | null = getPageForToken(pages, token);
        while (token < range.toTok && !pageAndRange) {
            pageAndRange = getPageForToken(pages, token);
            ++token;
        }

        if (!pageAndRange) {
            return true;
        }

        const view = this.queries.view.query!.single()?.getComponent<ViewComponent>(ComponentId.View);
        if (!view) {
            return false;
        }

        const { page, tokenRange: pageTokenRange } = pageAndRange;
        const scroller = this._scroller.current!;
        if (!(this.resources.get("pageLoadState").value[page.id] & PageData.TokenLocations)) {
            // We need to load the tokens for this page yet so scroll to the target page to force a load
            scroller.scrollTo({top: scaleValue(page.position.y, view.worldToViewScale), behavior: 'auto'})
            return false;
        }

        const tokens = this.resources.get("pageTokens").value;
        let targetFromTok = Math.max(pageTokenRange.fromTok, range.fromTok);
        let targetToTok = Math.min(pageTokenRange.toTok + 1, range.toTok);

        if (!rangeContainsTokenBounds(targetFromTok, targetToTok, tokens)) {
            // There are no token bounds in this range - expand the range until we find one
            while ((targetFromTok > pageTokenRange.fromTok || targetToTok < pageTokenRange.toTok + 1) && !tokens[targetFromTok] && !tokens[targetToTok]) {
                if (targetFromTok > pageTokenRange.fromTok) {
                    targetFromTok--;
                }

                if (targetToTok < pageTokenRange.toTok + 1) {
                    targetToTok++;
                }
            }

            if (!rangeContainsTokenBounds(targetFromTok, targetToTok, tokens)) {
                // We've scrolled to the top of the page we believe this token exists on and that's as good as we can do
                return true;
            }
        }

        const targetPageRange: TokenRange = {
            fromTok: targetFromTok,
            toTok: Math.max(targetFromTok + 1, targetToTok)
        };

        const boxes = createBoundsForTokenRange(targetPageRange.fromTok, targetPageRange.toTok, tokens);
        const bounds = combineBoxes(boxes);
        const boundsTop = scaleValue(bounds.top, view.worldToViewScale);
        const boundsBottom = scaleValue(bounds.bottom, view.worldToViewScale);

        const isTopVisible = boundsTop >= scroller.scrollTop && boundsTop <= scroller.scrollTop + scroller.clientHeight;
        const isPartialVisible = rangesOverlap(scroller.scrollTop, scroller.scrollTop + scroller.clientHeight, boundsTop, boundsBottom);
        if (isTopVisible || (!scrollToTop && isPartialVisible)) {
            // Range already in view
            return true;
        }

        let scrollPos: number;
        if (range.toTok > pageTokenRange.toTok + 1) {
            scrollPos = boundsTop - 20;
        }
        else {
            const selectionHeight: number = boundsBottom - boundsTop;
            scrollPos = boundsTop - (scroller.clientHeight - selectionHeight) / 2
        }

        scroller.scrollTo({ top: Math.max(Math.floor(scrollPos), 0), behavior: 'auto' });
        return true;
    }
}

const rangeContainsTokenBounds = (fromTok: number, toTok: number, tokens: TokenIdToBox) => {
    for (let i = fromTok; i <= toTok; ++i) {
        if (!!tokens[i]) {
            return true;
        }
    }

    return false;
}
